import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Receipt, Plus, Check, X, Wallet, Filter, Image, FileText, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import api from '../lib/api';

const fmtCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-blue-100 text-blue-700',
};

const DailyExpenses = ({ user }) => {
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const [tab, setTab] = useState('list');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', category: 'travel', reason: '', payment_mode: 'cash', bill_photo_url: '' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [uploading, setUploading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadExpenses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await api.get(`/expenses?${params.toString()}`);
      setExpenses(res.data || []);
    } catch { toast.error('Failed to load expenses'); }
  }, [filterStatus, filterCategory, startDate, endDate]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await api.get(`/expenses/summary?start_date=${startDate}&end_date=${endDate}`);
      setSummary(res.data);
    } catch {}
  }, [startDate, endDate]);

  const loadCategories = useCallback(async () => {
    try { const r = await api.get('/expenses/categories'); setCategories(r.data || []); } catch {}
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadExpenses(); loadSummary(); }, [loadExpenses, loadSummary]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/expenses/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, bill_photo_url: res.data.url }));
      toast.success('Bill uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const submitExpense = async () => {
    if (!form.amount || form.amount <= 0) { toast.error('Enter valid amount'); return; }
    if (!form.reason || form.reason.length < 3) { toast.error('Reason required (min 3 chars)'); return; }
    try {
      await api.post('/expenses', { ...form, amount: parseFloat(form.amount) });
      toast.success('Expense submitted for approval');
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), amount: '', category: 'travel', reason: '', payment_mode: 'cash', bill_photo_url: '' });
      loadExpenses();
      loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
  };

  const approveExpense = async (id) => {
    try {
      await api.post(`/expenses/${id}/approve`, { notes: '' });
      toast.success('Approved');
      loadExpenses(); loadSummary();
    } catch { toast.error('Approve failed'); }
  };

  const submitReject = async () => {
    if (!rejectReason || rejectReason.length < 3) { toast.error('Reason required (min 3 chars)'); return; }
    try {
      await api.post(`/expenses/${rejectFor.id}/reject`, { rejection_reason: rejectReason });
      toast.success('Rejected');
      setRejectFor(null); setRejectReason('');
      loadExpenses();
    } catch { toast.error('Reject failed'); }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/expenses/${id}/mark-paid`);
      toast.success('Marked as paid');
      loadExpenses(); loadSummary();
    } catch { toast.error('Mark paid failed'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.delete(`/expenses/${id}`); toast.success('Deleted'); loadExpenses(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="w-7 h-7 text-orange-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Daily Expenses</h1>
            <p className="text-sm text-gray-500 mt-0.5">{isAdminOrManager ? 'All staff expenses · Approve/Reject/Pay' : 'Submit your daily expenses for approval'}</p>
          </div>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" /> Add Expense</Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Total ({summary.count})</p><p className="text-lg font-bold">{fmtCurrency(summary.total)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-amber-600">Pending</p><p className="text-lg font-bold text-amber-700">{fmtCurrency(summary.by_status.pending)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-emerald-600">Approved</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(summary.by_status.approved)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-blue-600">Paid</p><p className="text-lg font-bold text-blue-700">{fmtCurrency(summary.by_status.paid)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-red-600">Rejected</p><p className="text-lg font-bold text-red-600">{fmtCurrency(summary.by_status.rejected)}</p></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">Expenses</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-3 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-start" />
              <span className="text-xs">to</span>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-end" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="expenses-table">
                  <thead><tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left text-xs font-medium">Date</th>
                    {isAdminOrManager && <th className="px-3 py-2 text-left text-xs font-medium">User</th>}
                    <th className="px-3 py-2 text-center text-xs font-medium">Category</th>
                    <th className="px-3 py-2 text-right text-xs font-medium">Amount</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Mode</th>
                    <th className="px-3 py-2 text-left text-xs font-medium">Reason</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Bill</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Status</th>
                    <th className="px-3 py-2 text-center text-xs font-medium">Action</th>
                  </tr></thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr><td colSpan={isAdminOrManager ? 9 : 8} className="px-4 py-8 text-center text-gray-400">No expenses for this period</td></tr>
                    ) : expenses.map(e => (
                      <tr key={e.id} className="border-b hover:bg-gray-50" data-testid={`expense-row-${e.id}`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{e.date}</td>
                        {isAdminOrManager && <td className="px-3 py-2 text-xs">{e.user_name}</td>}
                        <td className="px-3 py-2 text-center"><Badge className="text-[9px] bg-gray-200 text-gray-700">{e.category}</Badge></td>
                        <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(e.amount)}</td>
                        <td className="px-3 py-2 text-center text-xs uppercase">{e.payment_mode}</td>
                        <td className="px-3 py-2 text-xs text-gray-700 max-w-[200px] truncate" title={e.reason}>{e.reason}</td>
                        <td className="px-3 py-2 text-center">
                          {e.bill_photo_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${e.bill_photo_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                              {e.bill_photo_url.endsWith('.pdf') ? <FileText className="w-3.5 h-3.5 inline" /> : <Image className="w-3.5 h-3.5 inline" />}
                            </a>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge className={`text-[10px] ${STATUS_COLOR[e.status]}`}>{e.status}</Badge>
                          {e.status === 'rejected' && e.rejection_reason && <p className="text-[9px] text-red-600 mt-0.5" title={e.rejection_reason}>({e.rejection_reason.slice(0, 25)}...)</p>}
                        </td>
                        <td className="px-3 py-2 text-center flex items-center justify-center gap-1">
                          {isAdminOrManager && e.status === 'pending' && (
                            <>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-emerald-600" onClick={() => approveExpense(e.id)} data-testid={`approve-${e.id}`}><Check className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-600" onClick={() => { setRejectFor(e); setRejectReason(''); }} data-testid={`reject-${e.id}`}><X className="w-3 h-3" /></Button>
                            </>
                          )}
                          {isAdminOrManager && e.status === 'approved' && (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-600" onClick={() => markPaid(e.id)} data-testid={`pay-${e.id}`}><Wallet className="w-3 h-3 mr-1" />Pay</Button>
                          )}
                          {(e.user_id === user?.id && e.status === 'pending') || isAdminOrManager ? (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500" onClick={() => deleteExpense(e.id)} data-testid={`del-${e.id}`}><Trash2 className="w-3 h-3" /></Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="space-y-3 mt-4">
          {summary && (
            <>
              {isAdminOrManager && summary.by_user.length > 0 && (
                <Card>
                  <CardContent className="p-0">
                    <div className="px-4 py-2 bg-gray-50 border-b font-bold text-sm">By Staff</div>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50 border-b">
                        <th className="px-3 py-2 text-left text-xs">User</th>
                        <th className="px-3 py-2 text-center text-xs">Entries</th>
                        <th className="px-3 py-2 text-right text-xs">Total</th>
                      </tr></thead>
                      <tbody>
                        {summary.by_user.map(u => (
                          <tr key={u.user_id} className="border-b">
                            <td className="px-3 py-2 text-xs">{u.user_name}</td>
                            <td className="px-3 py-2 text-center text-xs">{u.count}</td>
                            <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(u.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
              {summary.by_category.length > 0 && (
                <Card>
                  <CardContent className="p-0">
                    <div className="px-4 py-2 bg-gray-50 border-b font-bold text-sm">By Category</div>
                    <table className="w-full text-sm">
                      <tbody>
                        {summary.by_category.map(c => (
                          <tr key={c.category} className="border-b">
                            <td className="px-3 py-2 text-xs capitalize">{c.category.replace(/_/g, ' ')}</td>
                            <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(c.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Expense dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="add-expense-dialog">
          <DialogHeader><DialogTitle>Add Daily Expense</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="exp-date" />
              </div>
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="exp-amount" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={v => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="What was this for?" data-testid="exp-reason" />
            </div>
            <div>
              <Label className="text-xs">Bill / Receipt (optional)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={handleUpload} disabled={uploading} data-testid="exp-bill-upload" />
              {form.bill_photo_url && <p className="text-[10px] text-emerald-600 mt-1">✓ Uploaded</p>}
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button className="bg-orange-600 hover:bg-orange-700" onClick={submitExpense} data-testid="submit-expense-btn">Submit</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Expense</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-gray-500">{rejectFor?.user_name} · {rejectFor && fmtCurrency(rejectFor.amount)} · {rejectFor?.category}</p>
            <div>
              <Label className="text-xs">Rejection Reason</Label>
              <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Why is this rejected?" data-testid="reject-reason-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={submitReject} data-testid="submit-reject-btn">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyExpenses;
